import React, { useState, useMemo } from 'react';
import { Table, Button, Form, Pagination, Row, Col } from 'react-bootstrap';
import { turnoMap, User } from '../../../../../service/types/types';
import UserModal from '../../predioPrograma/modals/user.modal';

type UserTableProps = {
  users: User[];
  onSave: (isOk: boolean) => void;
  onGo: (lat: number, lng: number) => void;
};

export default function UserTable({ users, onSave, onGo }: UserTableProps) {
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 5; // 👈 cantidad de filas por página

  // Filtrar usuarios según búsqueda
  const filteredUsers = useMemo(() => {
    return users.filter((u) =>{
      const turnoLabel = turnoMap[u.turno.toUpperCase()] ?? "";

      return [u.rut, u.name, turnoLabel].some((field) =>
        field?.toLowerCase().includes(search.toLowerCase())
      )
    });
  }, [users, search]);

  // Calcular paginación
  const totalPages = Math.ceil(filteredUsers.length / rowsPerPage);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const getPageNumbers = (totalPages: number, currentPage: number, delta = 2) => {
    const range: (number | string)[] = [];
    const rangeWithDots: (number | string)[] = [];
    let l: number | undefined; // 👈 allow undefined initially

    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
        range.push(i);
      }
    }

    for (let i of range) {
      if (l) {
        if (i as number - l === 2) {
          rangeWithDots.push(l + 1);
        } else if (i as number - l !== 1) {
          rangeWithDots.push('...');
        }
      }
      rangeWithDots.push(i);
      l = i as number;
    }

    return rangeWithDots;
  };
  
  return (
    <div style={{ height: '50vh', overflowY: 'auto', padding: '1rem' }}>
      <div className="p-3">
        <Row>
          <Col className="d-flex align-items-center"><h4>Trabajadores</h4></Col>
        </Row>
      </div>
      {/* 🔎 Buscador */}
      <Form.Control
        type="text"
        placeholder="Buscar por nombre, rut o email..."
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setCurrentPage(1); // resetear a primera página al buscar
        }}
        className="mb-3"
      />

      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>Run</th>
            <th>Nombre</th>
            <th>Telefono</th>
            <th>Localidad</th>
            <th>Email</th>
            <th>Turno</th>
            <th style={{ width: '220px' }}>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {paginatedUsers.map((user) => (
            <tr key={user.rut}>
              <td>{user.rut}</td>
              <td>{user.name}</td>
              <td>{user.phone}</td>
              <td>{user.localidad?.name}</td>
              <td>{user.email}</td>
              <td>{turnoMap[user.turno] ?? user.turno}</td>
              <td>
                {user.localidad && (
                  <Button
                    variant="info"
                    size="sm"
                    className="me-2"
                    onClick={() =>
                      onGo(
                        user.localidad.location.coordinates[1],
                        user.localidad.location.coordinates[0]
                      )
                    }
                  >
                    Ir
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* 📄 Paginación */}
      <Pagination>
        <Pagination.Prev
          disabled={currentPage === 1}
          onClick={() => setCurrentPage((p) => p - 1)}
        />

        {getPageNumbers(totalPages, currentPage).map((page, idx) =>
          page === '...' ? (
            <Pagination.Ellipsis key={idx} disabled />
          ) : (
            <Pagination.Item
              key={`page-${page}-${idx}`} 
              active={page === currentPage}
              onClick={() => setCurrentPage(page as number)}
            >
              {page}
            </Pagination.Item>
          )
        )}

        <Pagination.Next
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage((p) => p + 1)}
        />
      </Pagination>
    </div>
  );
}